import Color from 'color';
import { presetColorGradientAvailable, presetColorSwatch } from 'spw-constants';
import _ from 'lodash';

type RGBColor = {
  r: number;
  g: number;
  b: number;
};

export const getTransparentColor = (color: string, alpha?: number) =>
  Color(color)
    .rgb()
    .alpha(alpha || 0.4)
    .toString();

const GRADIENT_DIFF = 0.05;

// code below is lifted from https://github.com/edelstone/tints-and-shades/blob/master/scripts/tints-and-shades.js

// shade one of our rgb color objects to a distance of i*GRADIENT_DIFF%
// ({ red: 80, green: 18, blue: 20 }, 1) => { red: 72, green: 16, blue: 18 }
const rgbShade = (rgb: RGBColor, i: number, gradientDiff: number) => {
  return {
    r: rgb.r * (1 - gradientDiff * i),
    g: rgb.g * (1 - gradientDiff * i),
    b: rgb.b * (1 - gradientDiff * i),
  };
};

// tint one of our rgb color objects to a distance of i*GRADIENT_DIFF%
// ({ red: 80, green: 18, blue: 20 }, 1) => { red: 98, green: 42, blue: 44 }
const rgbTint = (rgb: RGBColor, i: number, gradientDiff: number) => {
  return {
    r: rgb.r + (255 - rgb.r) * i * gradientDiff,
    g: rgb.g + (255 - rgb.g) * i * gradientDiff,
    b: rgb.b + (255 - rgb.b) * i * gradientDiff,
  };
};

export const buildColorGradient = (
  baseColor: string,
  options = { useOldAlgorithm: false }
): string[] => {
  if (!baseColor) return [];
  let gradientColors: string[];

  const gradientDiff = options.useOldAlgorithm ? 0.1 : GRADIENT_DIFF;

  if (presetColorGradientAvailable(baseColor.toUpperCase())) {
    gradientColors = presetColorSwatch(baseColor.toUpperCase());
  } else {
    const color = Color(baseColor).object();
    const shadeValues = [];

    for (let i = 0; i < 20; i++) {
      shadeValues[i] = Color(
        i < 11
          ? rgbTint(color as RGBColor, i, gradientDiff)
          : rgbShade(color as RGBColor, i - 10, gradientDiff)
      ).hex();
    }
    const tints = shadeValues.slice(0, 11);
    const shades = shadeValues.slice(11, 20);
    gradientColors = tints.reverse().concat(shades);
  }
  return gradientColors;
};

export const getColorArray = (
  selectedColors: string[],
  gradientColors: string[],
  maxSize: number
) => {
  let colorArray = _(selectedColors)
    .slice(0, maxSize)
    .sort(
      (a: string, b: string) =>
        gradientColors.indexOf(a) - gradientColors.indexOf(b)
    )
    .value();

  if (colorArray.length < maxSize) {
    colorArray = colorArray.concat(
      _.fill(Array(maxSize - colorArray.length), null)
    );
  }

  return colorArray;
};
